/**
 * Shared GHS / OSHA HazCom hazard-classification metadata.
 *
 * Single source of truth for the Tier II hazard-pill UI that is reused by the
 * product forms and the facility report form. Keeping the keys, labels, and
 * ordering here prevents the three call sites from drifting apart.
 */

/** The canonical list of boolean hazard-classification fields stored per product/compound. */
export const HAZARD_KEYS = [
  'hazFlammable',
  'hazGasUnderPressure',
  'hazExplosive',
  'hazSelfHeating',
  'hazPyrophoric',
  'hazSelfReactive',
  'hazOrganicPeroxide',
  'hazOxidizer',
  'hazCorrosiveToMetal',
  'hazCombustibleDust',
  'hazOtherPhysical',
  'hazAcuteToxicity',
  'hazSkinCorrosion',
  'hazSeriousEyeDamage',
  'hazRespiratorySensitizer',
  'hazGermCellMutagenicity',
  'hazCarcinogenicity',
  'hazReproductiveToxicity',
  'hazStotSingleExposure',
  'hazStotRepeatedExposure',
  'hazAspirationHazard',
  'hazSimpleAsphyxiant',
  'hazHnoc',
] as const;

export type HazKey = (typeof HAZARD_KEYS)[number];
export type HazardFlags = Record<HazKey, boolean>;

/** Physical state plus the full set of GHS hazard flags for one product or compound. */
export type HazardClassification = { physicalState: string | null } & HazardFlags;

/** Chemical reference metadata used to determine Tier II reportability. */
export type TierIIChemicalMetadata = HazardClassification & {
  isEHS: boolean;
  ehsTpqLbs: number | null;
  tier2ThresholdLbs: number | null;
  /** OSHA hazardous chemical (HCS 2012). Base EPCRA § 311/312 reportability trigger. */
  isHazardousChemical: boolean;
};

/** Physical-state dropdown options. */
export const PHYSICAL_STATE_OPTIONS: { value: string; label: string }[] = [
  { value: 'solid', label: 'Solid' },
  { value: 'liquid', label: 'Liquid' },
  { value: 'gas', label: 'Gas' },
  { value: 'mixture', label: 'Mixture' },
];

/** GHS physical-hazard pills, in display order. */
export const PHYSICAL_HAZARDS: { key: HazKey; label: string }[] = [
  { key: 'hazExplosive', label: 'Explosive' },
  { key: 'hazFlammable', label: 'Flammable (gases, aerosols, liquids, or solids)' },
  { key: 'hazOxidizer', label: 'Oxidizer (liquid, solid, or gas)' },
  { key: 'hazSelfReactive', label: 'Self-reactive' },
  { key: 'hazPyrophoric', label: 'Pyrophoric (liquid or solid)' },
  { key: 'hazSelfHeating', label: 'Self-heating' },
  { key: 'hazOrganicPeroxide', label: 'Organic peroxide' },
  { key: 'hazCorrosiveToMetal', label: 'Corrosive to metal' },
  { key: 'hazGasUnderPressure', label: 'Gas under pressure (compressed gas)' },
  { key: 'hazOtherPhysical', label: 'In contact with water emits flammable gas' },
  { key: 'hazCombustibleDust', label: 'Combustible Dust' },
];

/** GHS health-hazard pills, in display order. */
export const HEALTH_HAZARDS: { key: HazKey; label: string }[] = [
  { key: 'hazAcuteToxicity', label: 'Acute toxicity (any route of exposure)' },
  { key: 'hazSkinCorrosion', label: 'Skin corrosion or irritation' },
  { key: 'hazSeriousEyeDamage', label: 'Serious eye damage or eye irritation' },
  { key: 'hazRespiratorySensitizer', label: 'Respiratory or skin sensitization' },
  { key: 'hazGermCellMutagenicity', label: 'Germ cell mutagenicity' },
  { key: 'hazCarcinogenicity', label: 'Carcinogenicity' },
  { key: 'hazReproductiveToxicity', label: 'Reproductive toxicity' },
  { key: 'hazStotSingleExposure', label: 'Specific target organ toxicity – single exposure' },
  { key: 'hazStotRepeatedExposure', label: 'Specific target organ toxicity – repeated exposure' },
  { key: 'hazAspirationHazard', label: 'Aspiration hazard' },
  { key: 'hazSimpleAsphyxiant', label: 'Simple Asphyxiant' },
];

/** Catch-all hazard pills that do not fit the physical/health groups. */
export const OTHER_HAZARDS: { key: HazKey; label: string }[] = [
  { key: 'hazHnoc', label: 'Hazard Not Otherwise Classified (HNOC)' },
];

/**
 * Global Font Awesome icon per GHS hazard flag. Values are the bare icon token
 * (e.g. `fa-fire`); pair with a weight class (`fa-solid`, `fa-light`, …) at the
 * call site, e.g. `<i :class="['fa-solid', HAZARD_ICONS[key]]" />`.
 * Single source of truth so every screen renders the same symbol per hazard.
 */
export const HAZARD_ICONS: Record<HazKey, string> = {
  // Physical hazards
  hazExplosive: 'fa-burst',
  hazFlammable: 'fa-fire',
  hazOxidizer: 'fa-fire-flame-curved',
  hazSelfReactive: 'fa-atom',
  hazPyrophoric: 'fa-fire-flame-simple',
  hazSelfHeating: 'fa-temperature-arrow-up',
  hazOrganicPeroxide: 'fa-flask',
  hazCorrosiveToMetal: 'fa-droplet',
  hazGasUnderPressure: 'fa-gauge-high',
  hazOtherPhysical: 'fa-water',
  hazCombustibleDust: 'fa-smog',
  // Health hazards
  hazAcuteToxicity: 'fa-skull-crossbones',
  hazSkinCorrosion: 'fa-hand-dots',
  hazSeriousEyeDamage: 'fa-eye',
  hazRespiratorySensitizer: 'fa-lungs',
  hazGermCellMutagenicity: 'fa-dna',
  hazCarcinogenicity: 'fa-disease',
  hazReproductiveToxicity: 'fa-person-pregnant',
  hazStotSingleExposure: 'fa-user-injured',
  hazStotRepeatedExposure: 'fa-heart-pulse',
  hazAspirationHazard: 'fa-head-side-cough',
  hazSimpleAsphyxiant: 'fa-mask-ventilator',
  // Other
  hazHnoc: 'fa-triangle-exclamation',
};

/** A blank set of hazard flags with every flag cleared. */
export function emptyHazardFlags(): HazardFlags {
  return Object.fromEntries(HAZARD_KEYS.map((key) => [key, false])) as HazardFlags;
}

/** A blank hazard classification with every flag cleared. */
export function emptyHazardClassification(): HazardClassification {
  return { physicalState: null, ...emptyHazardFlags() };
}

/** Extract every hazard flag from a wider record, defaulting missing flags to false. */
export function pickHazardFlags(src: Partial<HazardFlags> | null | undefined): HazardFlags {
  return Object.fromEntries(HAZARD_KEYS.map((key) => [key, src?.[key] ?? false])) as HazardFlags;
}

/** Extract only classification values that are present, for patch/update operations. */
export function pickPresentHazardClassification(
  src: Partial<HazardClassification> | null | undefined,
): Partial<HazardClassification> {
  const result: Partial<HazardClassification> = {};
  if (src?.physicalState !== undefined) result.physicalState = src.physicalState;
  for (const key of HAZARD_KEYS) {
    if (src?.[key] !== undefined) result[key] = src[key];
  }
  return result;
}

/**
 * Extract a {@link HazardClassification} from a wider record (e.g. a product
 * brief or details object), defaulting any missing field to its blank value.
 */
export function pickHazardClassification(src: Partial<HazardClassification> | null | undefined): HazardClassification {
  return { physicalState: src?.physicalState ?? null, ...pickHazardFlags(src) };
}
